import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@mzic/mzic-environments';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { withCollectionRequestContext } from '../interceptors/collection-request-context';
import {
  CollectionEnvelopeDto,
  ConnectionEntity,
  RevokeCollectionResponse,
  RevokeRevisionShareResponse,
  RevisionShareTargetRequest,
  RevisionShareTargetResponse,
  RevisionTargetShare,
} from '../interfaces/collection.interface';

@Injectable({
  providedIn: 'root',
})
export class CollectionService {
  private readonly http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiUrl}/api/chordium`;

  /**
   * Get API base URL from environment
   */
  getApiUrl(): string {
    return this.apiUrl;
  }

  getCollectionEnvelope(
    collectionId: string,
  ): Observable<CollectionEnvelopeDto> {
    const exactCollectionId = requireCollectionId(collectionId);
    return this.http
      .get<unknown>(
        `${this.getApiUrl()}/v1/collections/${encodeURIComponent(
          exactCollectionId,
        )}`,
        { context: withCollectionRequestContext(exactCollectionId) },
      )
      .pipe(map(decodeCollectionEnvelope));
  }

  /**
   * GET /v1/connection-entities
   * Fetch available connection entities (providers) filtered by revenue type/subtype
   */
  getConnectionEntities(
    revenueType: string,
    revenueSubtype: string | null,
    collectionId: string,
  ): Observable<ConnectionEntity[]> {
    const params: Record<string, string> = {
      active_only: 'true',
      revenue_type: revenueType,
    };
    const normalizedRevenueSubtype = revenueSubtype?.trim();

    if (
      normalizedRevenueSubtype &&
      normalizedRevenueSubtype.toLowerCase() !== 'null'
    ) {
      params['revenue_subtype'] = normalizedRevenueSubtype;
    }

    return this.http.get<ConnectionEntity[]>(
      `${this.getApiUrl()}/v1/connection-entities`,
      {
        params,
        context: withCollectionRequestContext(collectionId),
      },
    );
  }

  /**
   * GET /v1/connection-entities/{entityId}
   * Fetch one connection entity by canonical ID path
   */
  getConnectionEntityById(
    entityId: string,
    collectionId: string,
  ): Observable<ConnectionEntity> {
    return this.http.get<ConnectionEntity>(
      `${this.getApiUrl()}/v1/connection-entities/${entityId}`,
      {
        context: withCollectionRequestContext(collectionId),
      },
    );
  }

  /**
   * POST /v1/collections/{collectionId}/revoke
   * Revoke collection and invalidate all tokens
   * Requires authentication token
   */
  revokeCollection(collectionId: string): Observable<RevokeCollectionResponse> {
    return this.http.post<RevokeCollectionResponse>(
      `${this.getApiUrl()}/v1/collections/${collectionId}/revoke`,
      {},
      { context: withCollectionRequestContext(collectionId) },
    );
  }

  createRevisionShare(
    collectionId: string,
    revisionId: string,
    request: RevisionShareTargetRequest,
  ): Observable<RevisionShareTargetResponse> {
    const exactCollectionId = requireCollectionId(collectionId);
    const exactRevisionId = requireRequiredId(revisionId, 'revisionId');
    return this.http.post<RevisionShareTargetResponse>(
      `${this.getApiUrl()}/v1/collections/${encodeURIComponent(
        exactCollectionId,
      )}/targets/share`,
      { ...request, revision_id: exactRevisionId },
      { context: withCollectionRequestContext(exactCollectionId) },
    );
  }

  getRevisionTargetShares(
    collectionId: string,
    revisionId: string,
  ): Observable<readonly RevisionTargetShare[]> {
    const exactCollectionId = requireCollectionId(collectionId);
    const exactRevisionId = requireRequiredId(revisionId, 'revisionId');
    return this.http.get<readonly RevisionTargetShare[]>(
      `${this.getApiUrl()}/v1/collections/${encodeURIComponent(
        exactCollectionId,
      )}/targets/shares`,
      {
        params: { revision_id: exactRevisionId },
        context: withCollectionRequestContext(exactCollectionId),
      },
    );
  }

  revokeRevisionShare(
    collectionId: string,
    revisionId: string,
    shareId: string,
  ): Observable<RevokeRevisionShareResponse> {
    const exactCollectionId = requireCollectionId(collectionId);
    const exactRevisionId = requireRequiredId(revisionId, 'revisionId');
    const exactShareId = requireRequiredId(shareId, 'shareId');
    return this.http.post<RevokeRevisionShareResponse>(
      `${this.getApiUrl()}/v1/collections/${encodeURIComponent(
        exactCollectionId,
      )}/targets/shares/${encodeURIComponent(exactShareId)}/revoke`,
      {},
      {
        params: { revision_id: exactRevisionId },
        context: withCollectionRequestContext(exactCollectionId),
      },
    );
  }
}

function requireCollectionId(collectionId: string | undefined): string {
  if (!collectionId?.trim()) {
    throw new TypeError('collectionId must be a non-empty string');
  }
  return collectionId.trim();
}

function requireRequiredId(value: string, field: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${field} must be a non-empty string`);
  }
  return value.trim();
}

function decodeCollectionEnvelope(value: unknown): CollectionEnvelopeDto {
  if (!isPlainRecord(value)) {
    throw new TypeError('Collection envelope is invalid.');
  }
  if ('targets' in value) {
    throw new TypeError('Collection envelope contains runtime target state.');
  }
  if (
    typeof value['id'] !== 'string' ||
    !['ACTIVE', 'CANCELLED', 'EXPIRED'].includes(String(value['status'])) ||
    !(value['alias'] === null || typeof value['alias'] === 'string') ||
    !isNullablePartner(value['partner']) ||
    !isPlainRecord(value['integration']) ||
    Object.keys(value['integration']).some((key) => key !== 'enabled') ||
    typeof value['integration']['enabled'] !== 'boolean' ||
    typeof value['is_owner'] !== 'boolean' ||
    !isNullableString(value['individual_id']) ||
    !isNullableString(value['business_id']) ||
    (value['entity_type'] !== 'INDIVIDUAL' &&
      value['entity_type'] !== 'BUSINESS')
  ) {
    throw new TypeError('Collection envelope fields are invalid.');
  }
  return {
    id: value['id'],
    status: value['status'] as CollectionEnvelopeDto['status'],
    alias: value['alias'],
    partner: value['partner'] as CollectionEnvelopeDto['partner'],
    integration: {
      enabled: value['integration']['enabled'],
    },
    is_owner: value['is_owner'],
    individual_id:
      value['individual_id'] as CollectionEnvelopeDto['individual_id'],
    business_id: value['business_id'] as CollectionEnvelopeDto['business_id'],
    entity_type: value['entity_type'],
  };
}

function isNullablePartner(value: unknown): boolean {
  if (value === null) return true;
  if (!isPlainRecord(value)) return false;
  const allowedKeys = new Set([
    'id',
    'name',
    'display_name',
    'icon_url',
    'description',
    'metadata',
    'active',
  ]);
  return (
    !Object.keys(value).some((key) => !allowedKeys.has(key)) &&
    typeof value['id'] === 'string' &&
    typeof value['name'] === 'string' &&
    typeof value['display_name'] === 'string' &&
    isNullableString(value['icon_url']) &&
    isNullableString(value['description']) &&
    (value['metadata'] === null || isPlainRecord(value['metadata'])) &&
    typeof value['active'] === 'boolean'
  );
}

function isNullableString(value: unknown): boolean {
  return value === null || typeof value === 'string';
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
